<script setup>
import { computed } from 'vue';
import { emitter } from 'helpers/mitt';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { downloadBlob } from 'dashboard/helper/downloadHelper';

import MessageStatus from './MessageStatus.vue';
import MessageRating from './bubbles/Feedback/MessageRating.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isTranscriptMessage,
  isJarabotRateableMessage,
} from 'dashboard/helper/jarabotHelper';

const { t } = useI18n();

const {
  isAFacebookInbox,
  isALineChannel,
  isAPhoneChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnAllegroChannel,
  isAKauflandChannel,
  isAInstagramChannel,
  hasCsatSurveyOnEachMessageEnabled,
} = useInbox();

const {
  id,
  content,
  status,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
  contentType,
  conversationId,
  sender,
} = useMessageContext();

import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const isTranscript = computed(() =>
  isTranscriptMessage({
    private: isPrivate.value,
    contentAttributes: contentAttributes.value,
  })
);

const isRateable = computed(() =>
  isJarabotRateableMessage({
    sender: sender.value,
    messageType: messageType.value,
    private: isPrivate.value,
    contentAttributes: contentAttributes.value,
  })
);

const isInputCSAT = computed(() => {
  return (
    hasCsatSurveyOnEachMessageEnabled.value &&
    messageType.value === MESSAGE_TYPES.TEMPLATE &&
    contentType.value === 'input_csat'
  );
});
const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm a')
);

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;
  if (isAPhoneChannel.value) return false;

  if (isInputCSAT.value) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email, Allegro and Kaufland channels if they have
  // a source ID. Neither marketplace offers read receipts, and a reply only carries an id once
  // it is on the ticket or thread, so the id is the only delivery signal there is.
  if (
    isAnEmailChannel.value ||
    isAnAllegroChannel.value ||
    isAKauflandChannel.value
  )
    return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAInstagramChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  // All messages marked as delivered for the web widget inbox and API inbox once they are sent.
  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAInstagramChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});

// Widget/API conversation continuity: the reply mailer stamps the messages it really emailed.
const emailedAt = computed(() => {
  if (isPrivate.value) return null;
  const emailNotifiedAt = contentAttributes.value?.emailNotifiedAt;
  if (!emailNotifiedAt) return null;

  return messageTimestamp(
    Math.floor(new Date(emailNotifiedAt).getTime() / 1000),
    'LLL d, h:mm a'
  );
});

const isFacebookComment = computed(() => {
  return contentAttributes.value?.type === 'facebook_comment';
});

const openCommentTree = () => {
  emitter.emit(BUS_EVENTS.SHOW_COMMENT_TREE, { messageId: id.value });
};

const handleReplyTo = () => {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  LocalStorage.updateJsonStore(replyStorageKey, conversationId.value, id.value);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
};

const transcriptPlainText = () => {
  if (!content.value) return '';

  return content.value.replace(/\*\*(.+?)\*\*/g, '$1');
};

const downloadTranscript = () => {
  const text = transcriptPlainText();
  if (text) {
    const filename = `conversation_${conversationId.value}_transcript.txt`;
    downloadBlob(text, filename, 'text/plain');
  }
};

const copyTranscript = async () => {
  const text = transcriptPlainText();
  if (text) {
    await copyTextToClipboard(text);
    useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
  }
};

const useAsTemplate = () => {
  const insertIntoRichEditor = isAWebWidgetInbox || isAnEmailChannel;
  if (insertIntoRichEditor) {
    emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, content.value);
  } else {
    emitter.emit(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, content.value);
  }
};
</script>

<template>
  <div class="text-xs flex items-center gap-1.5">
    <div class="inline">
      <time class="inline">{{ readableTime }}</time>
    </div>
    <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
    <Icon
      v-if="contentAttributes.hidden"
      v-tooltip.top-start="$t('CONVERSATION.INVISIBLE_TO_CUSTOMER')"
      icon="i-lucide-eye-off"
      class="ml-auto size-3 text-n-slate-10"
    />
    <a v-if="isFacebookComment" href="#" @click.prevent="openCommentTree">
      <Icon
        v-tooltip.top-start="$t('CONVERSATION.COMMENT_TREE.VIEW_TREE')"
        icon="i-lucide-eye"
        class="size-3"
      />
    </a>
    <a v-if="isFacebookComment" href="#" @click.prevent="handleReplyTo">
      <Icon
        v-tooltip.top-start="$t('CONVERSATION.REPLYBOX.REPLY_TO_THIS')"
        icon="i-lucide-reply"
        class="size-3"
      />
    </a>
    <Icon
      v-if="emailedAt"
      v-tooltip.top-start="
        $t('CONVERSATION.SENT_VIA_EMAIL', { time: emailedAt })
      "
      icon="i-lucide-mail"
      class="size-3"
    />
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
    <a
      v-if="contentAttributes.jarabot?.copilot"
      href="#"
      @click.prevent="useAsTemplate"
    >
      <Icon
        v-tooltip.top-start="$t('CHAT_LIST.MESSAGE_USE_AS_TEMPLATE')"
        icon="i-lucide-message-square-share"
        class="size-3"
      />
    </a>
    <a v-if="isTranscript" href="#" @click.prevent="downloadTranscript">
      <Icon
        v-tooltip.top-start="$t('CONVERSATION.TRANSCRIPT.DOWNLOAD')"
        icon="i-lucide-download"
        class="size-3"
      />
    </a>
    <a v-if="isTranscript" href="#" @click.prevent="copyTranscript">
      <Icon
        v-tooltip.top-start="$t('CONVERSATION.TRANSCRIPT.COPY')"
        icon="i-lucide-clipboard-copy"
        class="size-3"
      />
    </a>
    <MessageRating v-if="isRateable" class="ml-auto" />
  </div>
</template>
`
